import type { Letter, Rng, SpawnerConfig, SpawnerState } from './types';

export interface SpawnContext {
  dt: number;
  activeCount: number;
  now: number;
  rng: Rng;
  issueId: () => string;
}

export interface SpawnTickResult {
  letters: Letter[];
  interval: number;
}

const ALPHABET = 'abcdefghijklmnopqrstuvwxyz';

function computeInterval(config: SpawnerConfig, spawned: number): number {
  if (config.initialInterval <= config.minInterval) {
    return Math.max(config.minInterval, config.initialInterval);
  }
  const ramp = Math.max(1, config.rampToMinAfter);
  const progress = Math.min(1, spawned / ramp);
  const delta = config.initialInterval - config.minInterval;
  return config.initialInterval - delta * progress;
}

const weightsCache = new WeakMap<SpawnerConfig, number[]>();

function buildWeights(bias: SpawnerConfig['letterBias']): number[] {
  const weights: number[] = [];
  for (let i = 0; i < ALPHABET.length; i += 1) {
    const letter = ALPHABET[i] as keyof NonNullable<SpawnerConfig['letterBias']>;
    const weight = bias?.[letter] ?? 1;
    weights.push(weight > 0 ? weight : 0);
  }
  const sum = weights.reduce((total, value) => total + value, 0);
  if (sum <= 0) {
    return new Array(ALPHABET.length).fill(1);
  }
  return weights;
}

function getWeights(config: SpawnerConfig): number[] {
  let weights = weightsCache.get(config);
  if (!weights) {
    weights = buildWeights(config.letterBias);
    weightsCache.set(config, weights);
  }
  return weights;
}

function pickLetter(rng: Rng, weights: number[]): string {
  const total = weights.reduce((sum, value) => sum + value, 0);
  const target = rng() * total;
  let cumulative = 0;
  for (let i = 0; i < weights.length; i += 1) {
    cumulative += weights[i] ?? 0;
    if (target <= cumulative) {
      return ALPHABET[i] ?? 'a';
    }
  }
  return ALPHABET[ALPHABET.length - 1];
}

function pickColumn(rng: Rng, columns: number): number {
  if (columns <= 1) {
    return 0;
  }
  const index = Math.floor(rng() * columns);
  if (index >= columns) {
    return columns - 1;
  }
  return index;
}

export function createSpawnerState(config: SpawnerConfig): SpawnerState {
  return {
    cooldown: config.initialInterval,
    spawned: 0,
  };
}

export function tickSpawner(
  config: SpawnerConfig,
  state: SpawnerState,
  context: SpawnContext
): SpawnTickResult {
  const weights = getWeights(config);
  const letters: Letter[] = [];
  let cooldown = state.cooldown - context.dt;
  const maxSpawnable = Math.max(0, config.maxConcurrent - context.activeCount);
  const interval = computeInterval(config, state.spawned);

  while (cooldown <= 0 && letters.length < maxSpawnable) {
    const columnIndex = pickColumn(context.rng, config.columns);
    const cellWidth = 1 / Math.max(1, config.columns);
    const x = (columnIndex + 0.5) * cellWidth;
    const letter: Letter = {
      id: context.issueId(),
      ch: pickLetter(context.rng, weights),
      x,
      y: config.spawnPadding,
      vy: config.initialVelocity,
      alive: true,
      column: columnIndex,
      spawnedAt: context.now,
    };
    letters.push(letter);
    state.spawned += 1;
    cooldown += interval;
  }

  state.cooldown = cooldown;

  return {
    letters,
    interval,
  };
}
